import React, { useEffect, useState, useRef } from 'react';
import { Search, Bell, ChevronDown, Sun, Moon, LogOut, User, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
interface TopBarProps {
  children?: React.ReactNode;
}
export const TopBar = ({
  children
}: TopBarProps) => {
  const {
    user,
    logout,
    switchRole
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const handleProfileToggle = () => {
    setProfileOpen(!profileOpen);
  };
  const handleRoleSwitch = (role: 'employee' | 'manager' | 'admin') => {
    switchRole(role);
    setProfileOpen(false);
  };
  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return <div className="h-16 border-b border-border bg-surface-50 flex items-center px-4 sticky top-0 z-10 shadow-subtle" data-id="element-89">
      <div className="flex items-center" data-id="element-90">{children}</div>
      <div className="flex-1 flex items-center justify-end md:justify-between" data-id="element-91">
        {/* Search - Full size on desktop, icon button on mobile */}
        <div className={`${searchOpen ? 'fixed inset-0 bg-surface-50 z-50 p-4 flex flex-col' : 'hidden md:block'} md:relative md:w-64 md:z-0`} data-id="element-92">
          {searchOpen && <div className="flex items-center justify-between mb-4" data-id="element-93">
              <h2 className="text-lg font-medium" data-id="element-94">Search</h2>
              <button onClick={() => setSearchOpen(false)} className="p-2 rounded-full hover:bg-surface-100" data-id="element-95">
                <X size={20} data-id="element-96" />
              </button>
            </div>}
          <div className="relative w-full" data-id="element-97">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-98">
              <Search className="h-4 w-4 text-text-tertiary" data-id="element-99" />
            </div>
            <input type="text" placeholder="Search files or employees..." className="block w-full pl-10 pr-3 py-2 border border-border rounded-input text-sm bg-surface-100 placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-brand-500" autoFocus={searchOpen} data-id="element-100" />
          </div>
        </div>
        {/* Actions */}
        <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4" data-id="element-101">
          <button className="p-2 rounded-full hover:bg-surface-100 text-text-secondary md:hidden" onClick={() => setSearchOpen(true)} data-id="element-102">
            <Search className="h-5 w-5" data-id="element-103" />
          </button>
          <button className="p-2 rounded-full hover:bg-surface-100 text-text-secondary relative" data-id="element-104">
            <Bell className="h-5 w-5" data-id="element-105" />
            <span className="absolute top-0 right-0 h-2 w-2 bg-error-500 rounded-full" data-id="element-106"></span>
          </button>
          <div className="relative" ref={profileRef} data-id="element-107">
            <button onClick={handleProfileToggle} className="flex items-center space-x-2 p-1 rounded-full hover:bg-surface-100" data-id="element-108">
              <img src={user?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80'} alt="User avatar" className="h-8 w-8 rounded-full object-cover border border-surface-200" data-id="element-109" />
              <div className="hidden md:block text-left" data-id="element-110">
                <p className="text-sm font-medium text-text-primary" data-id="element-111">
                  {user?.name}
                </p>
                <p className="text-xs text-text-secondary capitalize" data-id="element-112">
                  {user?.role}
                </p>
              </div>
              <ChevronDown className={`h-4 w-4 text-text-tertiary transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`} data-id="element-113" />
            </button>
            {profileOpen && <div className="absolute right-0 mt-2 w-56 rounded-card bg-surface-50 shadow-strong border border-border z-20 overflow-hidden" data-id="element-114">
                <div className="py-2" data-id="element-115">
                  {/* User info - mobile only */}
                  <div className="md:hidden px-4 py-2 border-b border-border" data-id="element-116">
                    <p className="text-sm font-medium text-text-primary" data-id="element-117">
                      {user?.name}
                    </p>
                    <p className="text-xs text-text-secondary capitalize" data-id="element-118">
                      {user?.role}
                    </p>
                  </div>
                  <button onClick={() => {}} className="flex w-full items-center px-4 py-2.5 text-sm text-text-primary hover:bg-surface-100" data-id="element-119">
                    <User className="mr-2 h-4 w-4" data-id="element-120" />
                    Profile
                  </button>
                  <button onClick={toggleTheme} className="flex w-full items-center px-4 py-2.5 text-sm text-text-primary hover:bg-surface-100" data-id="element-121">
                    {theme === 'light' ? <Moon className="mr-2 h-4 w-4" data-id="element-122" /> : <Sun className="mr-2 h-4 w-4" data-id="element-123" />}
                    {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                  </button>
                  <div className="border-t border-border my-1" data-id="element-124"></div>
                  <div className="px-4 py-1" data-id="element-125">
                    <p className="text-xs font-medium text-text-tertiary" data-id="element-126">
                      Switch role
                    </p>
                  </div>
                  <button onClick={() => handleRoleSwitch('employee')} className={`flex w-full items-center px-4 py-2.5 text-sm ${user?.role === 'employee' ? 'text-brand-600 bg-brand-50' : 'text-text-primary hover:bg-surface-100'}`} data-id="element-127">
                    Employee
                  </button>
                  <button onClick={() => handleRoleSwitch('manager')} className={`flex w-full items-center px-4 py-2.5 text-sm ${user?.role === 'manager' ? 'text-brand-600 bg-brand-50' : 'text-text-primary hover:bg-surface-100'}`} data-id="element-128">
                    Manager
                  </button>
                  <button onClick={() => handleRoleSwitch('admin')} className={`flex w-full items-center px-4 py-2.5 text-sm ${user?.role === 'admin' ? 'text-brand-600 bg-brand-50' : 'text-text-primary hover:bg-surface-100'}`} data-id="element-129">
                    Admin
                  </button>
                  <div className="border-t border-border my-1" data-id="element-130"></div>
                  <button onClick={logout} className="flex w-full items-center px-4 py-2.5 text-sm text-error-600 hover:bg-surface-100" data-id="element-131">
                    <LogOut className="mr-2 h-4 w-4" data-id="element-132" />
                    Sign out
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};